// ════════════════════════════════════════════════════════════════════════
//  Die Rechnung der Führung durch die ersten Schritte
//
//  Zwei Apps werden geführt: die Verwaltung (Lehrkraft und Schuladmin) und die
//  Admin-App (Redaktion). Ihre Stationen haben nichts gemeinsam – die eine legt
//  eine Klasse an, die andere sieht sich einen Lehrgang an. Ihre REGELN haben
//  alles gemeinsam, und die stehen hier.
//
//  WARUM DAS EIN PAKET IST UND KEINE ZWEITE KOPIE
//
//  „Das Lesezeichen ist eine Höchstmarke“, „alles vor der laufenden Station ist
//  erledigt“, „einen Knopf gibt es nur bei `done === null`“ sind Produktregeln,
//  keine App-Regeln. `Shell` und `Page` sind je App eigen und sollen es sein –
//  ein Rahmen gehört seiner App. Eine Regel gehört keiner.
//
//  Zweimal abgeschrieben driften sie, und die Wächtertests driften mit: eine
//  Kopie, die „fertig“ schon durch „später“ abhakt, fiele erst dem auf, der
//  beide Apps am selben Tag benutzt.
//
//  WAS HIER BEWUSST NICHT STEHT
//
//  Die Stationsliste, ihre Texte, ihre Marken und die Rollenfrage. `stepsFor`
//  gab es in der Verwaltung, weil dort zwei Rollen dieselbe Führung mit
//  unterschiedlich vielen Stationen laufen; die Admin-App kennt nur eine Rolle.
//  Die App filtert also selbst und übergibt die fertige Liste. Ein `role` im
//  Paket hieße, dass jede spätere App die Rollen der Verwaltung mitschleppt.
// ════════════════════════════════════════════════════════════════════════

/**
 * Eine Station.
 *
 * Generisch über den Schlüsseltyp und die Datenlage, damit jede App ihre
 * eigenen Schlüssel als Union führen kann und `done` typsicher auf ihre eigenen
 * Fakten sieht. Die Stationsliste bleibt damit DATEN: ablesbar, in einem Test
 * aufzuzählen, ohne Ausführung zu verstehen.
 */
export type TourStep<Key extends string, Facts> = {
  readonly key: Key;
  /** „Schritt 2 von 5“ zählt diese Nummer nicht – sie kommt aus der Position. */
  readonly title: string;
  readonly text: string;
  /** Route mit `:name`-Platzhaltern, oder null für eine Station ohne Ziel. */
  readonly route: string | null;
  /** Das Bedienelement, das hervorgehoben wird. Null: es gibt keines. */
  readonly mark: string | null;
  /**
   * Erledigt an echten Daten – oder null für eine Station, die nur angesehen
   * wird. Null ist zugleich die Antwort auf „gibt es hier einen Weiter-Knopf?“.
   */
  readonly done: ((facts: Facts) => boolean) | null;
};

/** Was von der Führung gespeichert ist. Kein Eintrag = noch nie angemeldet. */
export type TourState = {
  /** Das Lesezeichen. Null heißt: die Führung hat noch nicht angefangen. */
  readonly step: string | null;
  /**
   * Wann die Leiste weggelegt wurde. Null heißt: sie läuft. „Fertig“ und
   * „später“ sind derselbe Zustand – in beiden Fällen ist die Leiste weg und
   * der leise Verweis im Kopf holt sie zurück.
   */
  readonly doneAt: string | null;
};

/**
 * Eine Station mit ihrer Nummer und ihrem Haken.
 *
 * Über die STATION parametrisiert und nicht über Schlüssel und Fakten: die Apps
 * fassen `TourStep` enger (eine Marke aus einer Union, in der Verwaltung dazu
 * die Rolle). Käme hier der Grundtyp zurück, verlöre jede Prüfliste genau die
 * Verengung, für die es den eigenen Typ gibt – und die Marke wäre wieder
 * `string`.
 */
export type TourCheck<Step> = {
  readonly step: Step;
  /** Die sichtbare Nummer, 1-basiert. */
  readonly number: number;
  readonly done: boolean;
  /** Die Station, an der die Führung steht – höchstens eine ist es. */
  readonly current: boolean;
};

/**
 * Wo die Führung gerade steht.
 *
 * Die erste Station, die weder an echten Daten erledigt ist noch hinter dem
 * Lesezeichen liegt. Sind alle durch, ist es die letzte – die trägt `done: null`
 * und hält die Schleife von selbst an.
 */
export function currentStep<Facts, Step extends TourStep<string, Facts>>(
  steps: readonly Step[],
  state: TourState,
  facts: Facts,
): Step {
  // Ein Lesezeichen, das diese Fassung nicht kennt, zählt als „ganz vorn“:
  // `findIndex`-Ergebnis -1 wird zu 0. Die Alternative wäre ein Absturz an einer
  // Stelle, an der nichts kaputt ist – der Wert stammt aus einer älteren Fassung
  // der App und ist nur unbekannt, nicht falsch.
  const bookmark = Math.max(
    0,
    steps.findIndex((step) => step.key === state.step),
  );

  for (let i = 0; i < steps.length; i++) {
    const step = steps[i]!;
    if (i < bookmark) continue;
    if (step.done?.(facts)) continue;
    return step;
  }
  return steps[steps.length - 1]!;
}

/**
 * Alle Stationen mit Haken – die Prüfliste.
 *
 * Sie ist der ZWEITE Blick auf dieselbe Rechnung wie `currentStep`, und deshalb
 * leitet sie sich aus ihr ab statt daneben zu rechnen: **alles vor der laufenden
 * Station ist erledigt.** Genau daran ist `currentStep` ja vorbeigelaufen – eine
 * Liste, die eine Station davor als offen zeigt, widerspräche der Leiste über
 * ihr, und niemand könnte sagen, welche der beiden lügt.
 *
 * Das gilt auch, wenn die Datenlage etwas anderes sagt: wer seine Probeklasse
 * wieder löscht, hat die Station trotzdem hinter sich („die Führung geht nie
 * rückwärts“). Der Haken erzählt die Geschichte dieser Person, nicht den Stand
 * der Tabelle.
 *
 * Die letzte Station bekommt ihren Haken erst, wenn die Führung tatsächlich dort
 * abgeschlossen wurde. Ein „Später“ mittendrin setzt zwar auch `doneAt` – deshalb
 * zählt zusätzlich, dass die laufende Station die letzte ist. Ohne diese
 * Bedingung wäre die Führung schon dadurch „fertig“, dass jemand sie weggelegt
 * hat.
 */
export function tourChecklist<Facts, Step extends TourStep<string, Facts>>(
  steps: readonly Step[],
  state: TourState,
  facts: Facts,
): readonly TourCheck<Step>[] {
  const laufend = currentStep(steps, state, facts);
  const index = steps.findIndex((step) => step.key === laufend.key);

  return steps.map((step, i) => ({
    step,
    number: i + 1,
    done:
      i < index ||
      (step.done?.(facts) ?? false) ||
      (i === steps.length - 1 && i === index && state.doneAt !== null),
    current: i === index,
  }));
}

/**
 * Die Station nach dieser – das neue Lesezeichen, wenn jemand „Weiter“ drückt.
 * Auf der letzten bleibt es stehen: weiter als fertig geht es nicht.
 */
export function nextStepKey<Step extends { readonly key: string }>(
  steps: readonly Step[],
  key: Step["key"],
): Step["key"] {
  const index = steps.findIndex((step) => step.key === key);
  return steps[index + 1]?.key ?? steps[steps.length - 1]!.key;
}

/** „Schritt 2 von 5“ – Position und Länge, beide aus derselben Liste. */
export function stepPosition<Step extends { readonly key: string }>(
  steps: readonly Step[],
  key: Step["key"],
): { readonly number: number; readonly total: number } {
  return { number: steps.findIndex((step) => step.key === key) + 1, total: steps.length };
}

/**
 * Die Zieladresse einer Station.
 *
 * Die Platzhalter kommen als Abbildung herein und nicht als fester Name: die
 * Verwaltung setzt `:classId` ein, die Admin-App `:slug`. Ein fest verdrahteter
 * Name hätte den ersten Verwaltungsbegriff in dieses Paket geholt – und die
 * zweite App hätte ihn umgehen müssen.
 *
 * Fehlt ein Wert (null), gibt es keinen Link: ein Link ins Leere ist schlechter
 * als kein Link, denn er sieht aus wie ein Weg.
 */
export function tourRoute(
  step: { readonly route: string | null },
  params: Readonly<Record<string, string | null>>,
): string | null {
  const route = step.route;
  if (route === null) return null;

  let result = route;
  for (const [name, value] of Object.entries(params)) {
    const placeholder = `:${name}`;
    if (!result.includes(placeholder)) continue;
    if (value === null) return null;
    result = result.split(placeholder).join(value);
  }
  // Ein Platzhalter, für den niemand einen Wert mitgebracht hat, ist ein
  // Programmierfehler – aber kein Grund, eine kaputte Adresse anzubieten.
  return result.includes("/:") ? null : result;
}

/**
 * Läuft die Leiste gerade? Weggelegt ist weggelegt, egal ob durch „Später“
 * oder durch „Fertig“.
 */
export function tourRunning(state: TourState | null): boolean {
  return state === null || state.doneAt === null;
}
